"use client";

import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import javascript from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import { oneDark, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { stripBg } from "@/lib/utils";

/* Only the three languages these snippets actually use — PrismLight registers
   grammars explicitly so the bundle carries nothing else. */
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("python", python);

/* This block sits inside a `bg-card` panel whose tabs and chrome flip with the
   theme, so the syntax colors follow it rather than being pinned dark like the
   article code blocks (see components/ui/code-block.tsx, which is deliberately
   a fixed dark editor). Same treatment as the homepage API section.
   `stripBg` drops each theme's own background so the card's surface shows
   through; the comment hues are nudged for legibility on each ground. */
const darkTheme = {
  ...stripBg(oneDark),
  comment: { color: "#8b949e", fontStyle: "italic" as const },
  prolog: { color: "#8b949e" },
  doctype: { color: "#8b949e" },
  cdata: { color: "#8b949e" },
};
const lightTheme = {
  ...stripBg(oneLight),
  comment: { color: "#6b7280", fontStyle: "italic" as const },
  prolog: { color: "#6b7280" },
  doctype: { color: "#6b7280" },
  cdata: { color: "#6b7280" },
};

interface Props {
  code: string;
  language: string;
}

export default function DevCodeDisplay({ code, language }: Props) {
  const prefersLight = usePrefersLight();

  return (
    <SyntaxHighlighter
      language={language}
      style={prefersLight ? lightTheme : darkTheme}
      customStyle={{
        margin: 0,
        background: "transparent",
        padding: "1rem 1.25rem",
        fontSize: "0.8125rem",
        lineHeight: 1.7,
      }}
      codeTagProps={{ style: { fontFamily: "var(--font-mono, ui-monospace, monospace)" } }}
    >
      {code}
    </SyntaxHighlighter>
  );
}
